import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const leadSchema = z.object({
  page_slug: z.string().min(1).max(120),
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(255),
  company: z.string().trim().max(160).optional().or(z.literal("")),
  phone: z.string().trim().max(60).optional().or(z.literal("")),
  plan_interest: z.string().trim().max(160).optional().or(z.literal("")),
  message: z.string().trim().max(4000).optional().or(z.literal("")),
  budget_monthly_cents: z.number().int().nonnegative().max(100_000_000).optional().nullable(),
  timeline: z.string().trim().max(120).optional().or(z.literal("")),
  source: z.string().trim().max(160).optional().or(z.literal("")),
  user_agent: z.string().trim().max(500).optional().or(z.literal("")),
});

function esc(s: string | null | undefined) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!),
  );
}

async function resolveAdminEmail(admin: any): Promise<string | null> {
  // 1. explicit env override
  if (process.env.HOSTING_LEADS_ADMIN_EMAIL) return process.env.HOSTING_LEADS_ADMIN_EMAIL;
  // 2. site_settings.hosting_leads_admin_email or site_settings.contact_email
  try {
    const { data } = await admin
      .from("site_settings")
      .select("key,value")
      .in("key", ["hosting_leads_admin_email", "contact_email"]);
    const rows = (data ?? []) as Array<{ key: string; value: any }>;
    const pick = rows.find((r) => r.key === "hosting_leads_admin_email") ?? rows.find((r) => r.key === "contact_email");
    if (pick?.value) {
      const v = typeof pick.value === "string" ? pick.value : (pick.value?.email ?? pick.value?.value ?? "");
      if (v) return String(v);
    }
  } catch { /* ignore */ }
  // 3. first super_admin/admin user email
  try {
    const { data: roleRows } = await admin
      .from("user_roles")
      .select("user_id, role")
      .in("role", ["super_admin", "admin"])
      .limit(5);
    for (const r of (roleRows ?? []) as Array<{ user_id: string }>) {
      const { data: u } = await admin.auth.admin.getUserById(r.user_id);
      if (u?.user?.email) return u.user.email;
    }
  } catch { /* ignore */ }
  return null;
}

export const submitHostingLead = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => leadSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Insert lead (admin client bypasses RLS but policies also allow anon insert)
    const insertPayload = {
      page_slug: data.page_slug,
      name: data.name,
      email: data.email,
      company: data.company || null,
      phone: data.phone || null,
      plan_interest: data.plan_interest || null,
      message: data.message || null,
      budget_monthly_cents: data.budget_monthly_cents ?? null,
      timeline: data.timeline || null,
      source: data.source || null,
      user_agent: data.user_agent || null,
      status: "new",
    };
    const { data: inserted, error } = await supabaseAdmin
      .from("hosting_leads")
      .insert(insertPayload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    // Fire-and-forget email — do not block success on delivery
    const RESEND = process.env.RESEND_API_KEY;
    if (RESEND) {
      try {
        const to = await resolveAdminEmail(supabaseAdmin);
        if (to) {
          const budget =
            typeof data.budget_monthly_cents === "number"
              ? `$${(data.budget_monthly_cents / 100).toLocaleString()} / mo`
              : "—";
          const html = `
            <div style="font-family:system-ui,Segoe UI,Arial,sans-serif;max-width:640px;margin:auto;padding:24px;background:#ffffff;color:#0f172a">
              <h2 style="margin:0 0 8px">New hosting quote request</h2>
              <p style="margin:0 0 16px;color:#475569">From <strong>${esc(data.page_slug)}</strong></p>
              <table style="width:100%;border-collapse:collapse;font-size:14px">
                <tbody>
                  <tr><td style="padding:6px 0;color:#64748b">Name</td><td style="padding:6px 0"><strong>${esc(data.name)}</strong></td></tr>
                  <tr><td style="padding:6px 0;color:#64748b">Email</td><td style="padding:6px 0"><a href="mailto:${esc(data.email)}">${esc(data.email)}</a></td></tr>
                  <tr><td style="padding:6px 0;color:#64748b">Company</td><td style="padding:6px 0">${esc(data.company) || "—"}</td></tr>
                  <tr><td style="padding:6px 0;color:#64748b">Phone</td><td style="padding:6px 0">${esc(data.phone) || "—"}</td></tr>
                  <tr><td style="padding:6px 0;color:#64748b">Plan interest</td><td style="padding:6px 0">${esc(data.plan_interest) || "—"}</td></tr>
                  <tr><td style="padding:6px 0;color:#64748b">Budget</td><td style="padding:6px 0">${budget}</td></tr>
                  <tr><td style="padding:6px 0;color:#64748b">Timeline</td><td style="padding:6px 0">${esc(data.timeline) || "—"}</td></tr>
                </tbody>
              </table>
              <div style="margin-top:16px;padding:12px 14px;background:#f8fafc;border-radius:8px;white-space:pre-wrap">${esc(data.message) || "(no message)"}</div>
              <p style="margin-top:20px;color:#94a3b8;font-size:12px">Lead ID: ${inserted.id}</p>
            </div>`;
          await fetch("https://api.resend.com/emails", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${RESEND}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              from: "Hosting Leads <onboarding@resend.dev>",
              to: [to],
              reply_to: data.email,
              subject: `New quote request — ${data.name} (${data.page_slug})`,
              html,
            }),
          });
        }
      } catch {
        /* email is best-effort */
      }
    }

    return { ok: true, id: inserted.id as string };
  });
